/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  TreeSelect,
  DatePicker,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import getFormattedValues, { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import { DATE_FRONTEND_FORMAT } from 'utils/normalizers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import { getClasificadoresObjetoGastoUnion } from 'api/configuraciones/clasificadorObjetoGasto';
import { getCFFUnion } from 'api/catalogos/ClasificacionDeFuentesDeFinaciamientoInterna';
import { getTiposDeNominasEmpleados } from 'api/nominas/catalogos/tiposNominasEmpleados';
import { getTiposDeContribuciones } from 'api/catalogos/tiposDeContribuciones';
import { getIPEJALtasas } from 'api/nominas/IPEJALtasas';
import {
  getContribucionesPlaza,
  postContribucionPlaza,
  deleteContribucionPlaza,
  patchContribucionPlaza,
  getContribucionPlaza,
  autorizarContribucionPlaza,
} from 'api/catalogos/contribucionesPlaza';
import { checkToken } from 'api/utils';
import { ESTATUS_ACTIVO, ESTATUS_AUTORIZADO } from 'utils/constants';

const modelPermissionName = 'configuradorcontribucionplaza';

export const contribucionesPlazaPermission = {
  permissionModel: modelPermissionName,
  catalogsModels: [
    'clasificaciondeobjetodegastointerno',
    'clasificaciondefuentedefinanciamientointerna',
    'tipodenominaempleado',
    'tasasipejal',
  ],
  tabsModels: [],
};

/**
 * Arma el treeData de antd a partir de un endpoint "union": registros del
 * catálogo general (seleccionable: false) + internos de la entidad
 * (seleccionable: true). Los internos cuelgan del general vía clave_padre_id,
 * y los ids de ambas tablas pueden repetirse, por eso los padres llevan
 * prefijo en su key/value. Solo los internos son seleccionables y se omiten
 * las ramas generales que no contienen ningún interno.
 * @param {object[]} list
 * @param {string} labelProp
 */
const buildTreeData = (list = [], labelProp) => {
  const parentKey = (id) => `padre-${id}`;
  const nodes = new Map();
  list.forEach((item) => {
    const key = item.seleccionable ? item.id : parentKey(item.id);
    nodes.set(key, { ...item, key, children: [] });
  });

  const roots = [];
  nodes.forEach((node) => {
    const parent = node.clave_padre_id && node.key !== parentKey(node.clave_padre_id)
      ? nodes.get(parentKey(node.clave_padre_id)) : null;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const toTreeData = (items) => items
    .sort((a, b) => `${a.clave}`.localeCompare(`${b.clave}`))
    .map((node) => {
      if (node.seleccionable) {
        return {
          title: `${node.clave} - ${node[labelProp]}`,
          value: node.key,
          key: node.key,
        };
      }
      const children = toTreeData(node.children);
      if (!children.length) return null;
      return {
        title: `${node.clave} - ${node[labelProp]}`,
        value: node.key,
        key: node.key,
        disabled: true,
        children,
      };
    })
    .filter(Boolean);

  return toTreeData(roots);
};

const ConfiguradorDeContribucionesPlaza = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [cogsTree, setCogsTree] = useState([]);
  const [fuentesTree, setFuentesTree] = useState([]);
  const [tiposDeNominaEmpleado, setTiposDeNominaEmpleado] = useState([]);
  const [tiposDeContribuciones, setTiposDeContribuciones] = useState([]);
  const [tasasIPEJAL, setTasasIPEJAL] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [disableFields, setDisableFields] = useState(false);
  // Un registro Autorizado solo admite capturar o cambiar su Fecha Final.
  const [soloFechaFinal, setSoloFechaFinal] = useState(false);

  const formInstances = [form];

  const tipoDeContribucionId = Form.useWatch('tipo_de_contribucion', form);
  const requiereTasaIPEJAL = !!tiposDeContribuciones
    .find((t) => t.id === tipoDeContribucionId)?.requiere_tasa_ipejal;

  const fetchData = async () => {
    setLoading(true);
    const _data = await getContribucionesPlaza();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _cogs,
        _fuentes,
        _tiposDeNominaEmpleado,
        _tiposDeContribuciones,
        _tasasIPEJAL,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getClasificadoresObjetoGastoUnion(),
        getCFFUnion(),
        getTiposDeNominasEmpleados(),
        getTiposDeContribuciones(),
        getIPEJALtasas(),
        getContribucionesPlaza(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setCogsTree(buildTreeData(_cogs, 'concepto'));
        setFuentesTree(buildTreeData(_fuentes, 'nombre'));
        setTiposDeNominaEmpleado(_tiposDeNominaEmpleado);
        setTiposDeContribuciones(_tiposDeContribuciones);
        setTasasIPEJAL(_tasasIPEJAL.map((t) => ({ ...t, etiqueta: `${t.concepto} (${t.tasa}%)` })));
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setDisableFields(false);
    setSoloFechaFinal(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _contribucion = await getContribucionPlaza(match.id);

    setTimeout(() => {
      if (_contribucion) {
        form.resetFields();
        form.setFieldsValue(formatReceived(_contribucion));
        setDisableFields(_contribucion?.estados_globales !== ESTATUS_ACTIVO);
        setSoloFechaFinal(_contribucion?.estados_globales === ESTATUS_AUTORIZADO);
        setVisible(true);
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = getFormattedValues(form.getFieldsValue(), { clean: false });
      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchContribucionPlaza(key, values)
        : postContribucionPlaza(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteContribucionPlaza(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    form.resetFields();
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      const saved = await autorizarContribucionPlaza(key);
      if (saved) {
        const _data = await getContribucionesPlaza();
        setData(formatReceived(_data));
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Concepto',
      dataIndex: 'concepto',
      key: 'concepto',
      width: 250,
    },
    {
      titleText: 'Tipo de Contribución',
      dataIndex: 'tipo_de_contribucion_descripcion',
      key: 'tipo_de_contribucion_descripcion',
      width: 250,
      render: (val, record) => (record.tasa_ipejal_concepto
        ? `${val} — ${record.tasa_ipejal_concepto} (${record.tasa_ipejal_tasa}%)`
        : val),
    },
    {
      titleText: 'Clasificador por Objeto del Gasto',
      dataIndex: 'cog_clave',
      key: 'cog_clave',
      width: 300,
      render: (val, record) => (val ? `${val} - ${record.cog_descripcion}` : ''),
    },
    {
      titleText: 'Fuente de Financiamiento',
      dataIndex: 'fuente_clave',
      key: 'fuente_clave',
      width: 300,
      render: (val, record) => (val ? `${val} - ${record.fuente_descripcion}` : ''),
    },
    {
      titleText: 'Tipo de Nómina Empleado',
      dataIndex: 'tipos_de_nomina_empleado',
      key: 'tipos_de_nomina_empleado',
      width: 250,
      render: (val = []) => tiposDeNominaEmpleado
        .filter((t) => val.includes(t.id))
        .map((t) => t.descripcion)
        .join(', '),
    },
    {
      titleText: 'Fecha Final',
      dataIndex: 'fecha_final',
      key: 'fecha_final',
      width: 200,
      render: (val) => val?.format(DATE_FRONTEND_FORMAT),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    concepto: [requiredRule],
    tipo_de_contribucion: [requiredRule],
    tasa_ipejal: [requiredRule],
    cog: [requiredRule],
    fuente_financiamiento: [requiredRule],
    tipos_de_nomina_empleado: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          customActions={[
            {
              onClick: () => setVisibleAutorizar(true),
              permission: { model: modelPermissionName, action: 'autorizar' },
              icon: <CheckCircleOutlined />,
              text: 'Autorizar',
              disabledBySelection: true,
              disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
              className: 'success',
            },
          ]}
          disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Contribución Plaza`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields || soloFechaFinal}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            initialValues={{
              estados_globales: ESTATUS_ACTIVO,
            }}
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="concepto"
                label="Concepto"
                rules={rules.concepto}
                hasFeedback
              >
                <Input allowClear disabled={disableFields} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="tipo_de_contribucion"
                label="Tipo de Contribución"
                rules={rules.tipo_de_contribucion}
                hasFeedback
              >
                <Select
                  dataSource={tiposDeContribuciones}
                  keyProp="id"
                  labelProp="descripcion"
                  disabled={disableFields}
                  onChange={() => form.setFieldsValue({ tasa_ipejal: undefined })}
                />
              </Form.Item>
            </Col>

            {requiereTasaIPEJAL && (
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  name="tasa_ipejal"
                  label="Tasa IPEJAL"
                  rules={rules.tasa_ipejal}
                  hasFeedback
                >
                  <Select
                    dataSource={tasasIPEJAL}
                    keyProp="id"
                    labelProp="etiqueta"
                    disabled={disableFields}
                  />
                </Form.Item>
              </Col>
            )}

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="cog"
                label="Clasificador por Objeto del Gasto"
                rules={rules.cog}
                hasFeedback
              >
                <TreeSelect
                  showSearch
                  allowClear
                  treeNodeFilterProp="title"
                  style={{ width: '100%' }}
                  dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                  treeData={cogsTree}
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="fuente_financiamiento"
                label="Fuente de Financiamiento"
                rules={rules.fuente_financiamiento}
                hasFeedback
              >
                <TreeSelect
                  showSearch
                  allowClear
                  treeNodeFilterProp="title"
                  style={{ width: '100%' }}
                  dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                  treeData={fuentesTree}
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="tipos_de_nomina_empleado"
                label="Tipo de Nómina Empleado"
                rules={rules.tipos_de_nomina_empleado}
                hasFeedback
              >
                <Select
                  mode="multiple"
                  dataSource={tiposDeNominaEmpleado}
                  keyProp="id"
                  labelProp="descripcion"
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="fecha_final"
                label="Fecha Final"
              >
                <DatePicker
                  allowClear
                  format={DATE_FRONTEND_FORMAT}
                  style={{ width: '100%' }}
                  disabled={disableFields && !soloFechaFinal}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  // Siempre bloqueado: nace Activo, pasa a Autorizado con la acción
                  // Autorizar y a Inactivo al llegar su Fecha Final.
                  disabled
                />
              </Form.Item>
            </Col>

            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Contribución Plaza ${form.getFieldValue('concepto')}`}
          loading={loading}
        />

        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar la Contribución Plaza ${selectedData?.concepto}`}
          open={visibleAutorizar}
          loading={loading}
        />

      </Spin>
    </Row>
  );
};

export default ConfiguradorDeContribucionesPlaza;
